/**
 * DL-01 decision 6 — the size guard: warn above 10 GB, refuse above 25 GB
 * per download request. The SERVER decides (a 413 `download_too_large`, a
 * `large_download` warning); these mirrors only read what it said and keep
 * the two numbers in one place for the words. PURE.
 */

export const GB = 1024 ** 3;
export const WARN_BYTES = 10 * GB;
export const LIMIT_BYTES = 25 * GB;

export type SizeGuard = "ok" | "warn" | "blocked";

/**
 * `warn` when the server flagged `large_download` (or, defensively, the sum
 * crosses 10 GB without the flag); `blocked` is only ever the server's 413 —
 * a bundle in hand was accepted by the server, so this never says it.
 */
export function sizeGuardOf(
  totalBytes: number,
  warnings: readonly string[],
): SizeGuard {
  if (warnings.includes("large_download") || totalBytes > WARN_BYTES)
    return "warn";
  return "ok";
}

/** "12.3 GB" / "850 MB" / "12 kB" / "0 B" — base 1024, one decimal above MB. */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  if (n < 1024) return `${String(Math.round(n))} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${String(Math.round(kb))} kB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${String(Math.round(mb))} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(gb < 10 ? 2 : 1)} GB`;
}

/** "3 files" / "1 file" */
export function fileWord(n: number): string {
  return `${String(n)} ${n === 1 ? "file" : "files"}`;
}
